import { useEffect, useState } from 'react';

declare global {
  interface Window {
    googleMapsLoaded?: boolean;
  }
}

const loadPlacesLibrary = async (): Promise<boolean> => {
  if (window.google?.maps?.places) return true;

  if (window.google?.maps?.importLibrary) {
    try {
      await window.google.maps.importLibrary('places');
      return true;
    } catch {
      return !!window.google?.maps?.places;
    }
  }

  return false;
};

export const useGoogleMapsPlaces = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [placesReady, setPlacesReady] = useState(!!window.google?.maps?.places);

  useEffect(() => {
    if (placesReady) {
      setIsLoading(false);
      return;
    }

    if (window.googleMapsLoaded && window.google?.maps?.places) {
      setIsLoading(false);
      setPlacesReady(true);
      return;
    }

    if (window.googleMapsLoaded && window.google?.maps && !window.google?.maps?.places) {
      setIsLoading(false);
      return;
    }

    const handleMapsLoaded = async () => {
      if (window.google?.maps?.places) {
        setIsLoading(false);
        setPlacesReady(true);
      } else {
        try {
          const loaded = await loadPlacesLibrary();
          setPlacesReady(loaded);
          setIsLoading(false);
        } catch {
          setIsLoading(false);
          setPlacesReady(false);
        }
      }
    };

    window.addEventListener('googleMapsLoaded', handleMapsLoaded);

    return () => {
      window.removeEventListener('googleMapsLoaded', handleMapsLoaded);
    };
  }, [placesReady]);

  useEffect(() => {
    if (isLoading || placesReady) return;

    loadPlacesLibrary().then((loaded) => {
      setPlacesReady(loaded);
      if (!loaded) {
        setIsLoading(false);
      }
    });
  }, [isLoading, placesReady]);

  return {
    isReady: !isLoading && placesReady,
    isLoading
  };
};
